import React, { useState, useRef, useEffect } from "react";
import { X, Save } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import toast from "react-hot-toast";
import { getUserCustomization, updateUserCustomization } from "../../../api";

const CustomizeOGFLEET = ({ isOpen, onClose, isDarkTheme }) => {
  const { user, updateUserPreferences } = useAuth();
  const [nickname, setNickname] = useState("");
  const [profession, setProfession] = useState("");
  const [generalInstructions, setGeneralInstructions] = useState("");
  const [responseStyle, setResponseStyle] = useState("balanced");
  const [responseLength, setResponseLength] = useState("medium");

  const modalRef = useRef(null);

  useEffect(() => {
    const fetchCustomization = async () => {
      try {
        const customization = await getUserCustomization();
        setNickname(customization?.preferred_name || "");
        setProfession(customization?.profession || "");
        setResponseStyle(customization?.response_style || "balanced");
        setResponseLength(customization?.response_length || "medium");
        setGeneralInstructions(customization?.instructions || "");
      } catch (error) {
        toast.error("Failed to load preferences");
        console.error("Error fetching customization:", error);
      }
    };
  
    if (isOpen) {
      fetchCustomization();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const handleSave = async () => {
    try {
      await updateUserCustomization({
        preferred_name: nickname,
        profession,
        response_style: responseStyle,
        response_length: responseLength,
        instructions: generalInstructions,
      });
      toast.success("Preferences updated successfully!");
      onClose();
    } catch (error) {
      toast.error("Failed to update preferences");
      console.error("Error updating preferences:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[400]">
      <div
        ref={modalRef}
        className={`rounded-lg shadow-xl w-full max-w-xl mx-4 overflow-hidden ${
          isDarkTheme ? "bg-gray-800 text-gray-300" : "bg-white text-gray-800"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-8 py-6 border-b ${
            isDarkTheme ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2 className="text-xl font-semibold">Customize OGFLEET</h2>
          <button
            onClick={onClose}
            className={`${
              isDarkTheme
                ? "text-gray-300 hover:text-gray-100"
                : "text-gray-500 hover:text-gray-700"
            } focus:outline-none`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className={`px-8 py-6 max-h-[calc(100vh-200px)] overflow-y-auto ${
          isDarkTheme ? 
            "scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent" : 
            "scrollbar-thin scrollbar-track-transparent"
        }`}>
          <div className="space-y-8">
            {/* Personal Details */}
            <div>
              <h3 className="text-base font-medium mb-4">Personal Details</h3>
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="nickname"
                    className={`block text-sm mb-2 ${
                      isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    What should OGFLEET call you?
                  </label>
                  <input
                    id="nickname"
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="Your preferred name"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none text-base ${
                      isDarkTheme
                        ? "bg-gray-700 border-gray-600 text-gray-300"
                        : "border-gray-300 text-gray-800"
                    }`}
                  />
                </div>
                <div>
                  <label
                    htmlFor="profession"
                    className={`block text-sm mb-2 ${
                      isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    What do you do?
                  </label>
                  <input
                    id="profession"
                    type="text"
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                    placeholder="Your profession or role"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none text-base ${
                      isDarkTheme
                        ? "bg-gray-700 border-gray-600 text-gray-300"
                        : "border-gray-300 text-gray-800"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* General Instructions */}
            <div>
              <h3 className="text-base font-medium mb-4">General instructions for OG Gpt</h3>
              <div>
                <label
                  htmlFor="generalInstructions"
                  className={`block text-sm mb-2 ${
                    isDarkTheme ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Provide any general instructions or guidelines
                </label>
                <textarea
                  id="generalInstructions"
                  value={generalInstructions}
                  onChange={(e) => setGeneralInstructions(e.target.value)}
                  placeholder="Enter general instructions for OG Gpt"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none text-base ${
                    isDarkTheme
                      ? "bg-gray-700 border-gray-600 text-gray-300"
                      : "border-gray-300 text-gray-800"
                  }`}
                  rows={4}
                />
              </div>
            </div>

            {/* Response Preferences */}
            <div>
              <h3 className="text-base font-medium mb-4">Response Preferences</h3>
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="responseStyle"
                    className={`block text-sm mb-2 ${
                      isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Response Style
                  </label>
                  <select
                    id="responseStyle"
                    value={responseStyle}
                    onChange={(e) => setResponseStyle(e.target.value)}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none text-base ${
                      isDarkTheme
                        ? "bg-gray-700 border-gray-600 text-gray-300"
                        : "border-gray-300 text-gray-800"
                    }`}
                  >
                    <option value="precise">Precise - Technical and to the point</option>
                    <option value="balanced">Balanced - Mix of technical and conversational</option>
                    <option value="creative">Creative - More explanatory and instructional</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="responseLength"
                    className={`block text-sm mb-2 ${
                      isDarkTheme ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Response Length
                  </label>
                  <select
                    id="responseLength"
                    value={responseLength}
                    onChange={(e) => setResponseLength(e.target.value)}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent focus:outline-none text-base ${
                      isDarkTheme
                        ? "bg-gray-700 border-gray-600 text-gray-300"
                        : "border-gray-300 text-gray-800"
                    }`}
                  >
                    <option value="concise">Concise - Brief responses</option>
                    <option value="medium">Medium - Standard detail</option>
                    <option value="detailed">Detailed - Comprehensive responses</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`flex justify-end px-8 py-6 border-t ${
            isDarkTheme ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-6 py-3 text-base rounded-lg mr-3 focus:outline-none ${
              isDarkTheme
                ? "text-gray-300 hover:bg-gray-700"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-3 text-base bg-amber-500 text-white rounded-lg hover:bg-amber-600 focus:outline-none flex items-center"
          >
            <Save className="w-5 h-5 mr-2" />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomizeOGFLEET;
